import requestService from '@/services/request'

/**
 * Single shared Pusher connection for public channels (event, event-*) plus a
 * mandatory private-user subscription when the viewer is logged in.
 *
 * When Pusher "Authorized connections" is enabled on the app, each WebSocket must
 * authorize via at least one private/presence subscription within the timeout.
 * Separate `new Pusher()` instances that only subscribed to public channels hit 4009.
 *
 * @see https://pusher.com/docs/channels/using_channels/authorized-connections/
 */

/** Loaded on demand: pusher-js is ~140KB and no first paint needs a socket. */
let pusherModulePromise = null
function loadPusher() {
  if (!pusherModulePromise) {
    pusherModulePromise = import('pusher-js').then(mod => mod.default)
  }
  return pusherModulePromise
}

let sharedPusher = null
let lastSessionKey = undefined
let privateUserSubscribedFor = null

let mutex = Promise.resolve()

/**
 * Release shared client (e.g. session switch). Next ensure() builds a new socket.
 */
export function resetSharedPusher() {
  if (sharedPusher) {
    sharedPusher.disconnect()
    sharedPusher = null
  }
  lastSessionKey = undefined
  privateUserSubscribedFor = null
}

function sessionKeyFor(userId) {
  return userId != null ? String(userId) : 'anon'
}

async function ensureInner(userId) {
  const key = import.meta.env.VITE_PUSHER_APP_KEY
  const cluster = import.meta.env.VITE_PUSHER_APP_CLUSTER
  if (!key || !cluster) {
    return null
  }

  const nextKey = sessionKeyFor(userId)
  if (sharedPusher && lastSessionKey !== nextKey) {
    resetSharedPusher()
  }

  const token = userId != null ? await requestService.getAuthorizationToken() : null

  if (!sharedPusher) {
    const Pusher = await loadPusher()
    const options = { cluster }
    if (token) {
      options.forceTLS = true
      options.authEndpoint = requestService.getRequestUrl('/pusher/auth/')
      options.auth = {
        headers: {
          Authorization: token,
        },
      }
    }
    sharedPusher = new Pusher(key, options)
    lastSessionKey = nextKey
  }

  if (userId != null && token && privateUserSubscribedFor !== userId) {
    if (privateUserSubscribedFor != null && privateUserSubscribedFor !== userId) {
      try {
        sharedPusher.unsubscribe(`private-user-${privateUserSubscribedFor}`)
      } catch {
        /* noop */
      }
    }
    sharedPusher.subscribe(`private-user-${userId}`)
    privateUserSubscribedFor = userId
  }

  return sharedPusher
}

/**
 * @param {number|string|null|undefined} userId - Current user id; omit when logged out.
 * @returns {Promise<Pusher|null>}
 */
export function ensureSharedPusher(userId) {
  const run = async () => ensureInner(userId)
  const result = mutex.then(run)
  mutex = result.catch(() => {})
  return result
}

/** Sync access after at least one successful ensure (for teardown helpers). */
export function getSharedPusherSync() {
  return sharedPusher
}
